<script setup lang="ts">
import { useTemplateRef } from 'vue';

import { useAssistantMessageMarkdown } from '@/components/VisionAi/chat-panel-components/composables/useAssistantMessageMarkdown';
import { useMarkdownTableScrollbars } from '@/components/VisionAi/chat-panel-components/composables/useMarkdownTableScrollbars';
import type { VisionWidget } from '@/components/VisionAi/widgets/visionWidget.types';
import WidgetWrapper from '@/components/VisionAi/widgets/WidgetWrapper.vue';

interface Props {
  text?: string;
  widgets?: VisionWidget[];
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  widgets: () => [],
});

const emit = defineEmits<{
  messageInteraction: [interaction: string];
}>();

const { widgetMap, groupedSegments, renderMarkdown, handleMarkdownClick } =
  useAssistantMessageMarkdown(props, (interaction) => emit('messageInteraction', interaction));

const hostRef = useTemplateRef<HTMLElement>('host');
useMarkdownTableScrollbars(hostRef);

/**
 * Tailwind / dashing-ui only — no component style block.
 * Complements classes already set on tokens by useAssistantMessageMarkdown.
 */
const markdownHostClass = [
  'w-full min-w-0',
  // Task lists (`markdown-it-task-lists`) use `ul.contains-task-list`; do not
  // force `list-disc` on those or bullets show beside the checkboxes.
  '[&_ul:not(.contains-task-list)]:list-disc',
  '[&_ul.contains-task-list]:list-none',
  '[&_ol:not(.contains-task-list)]:list-decimal',
  '[&_ol.contains-task-list]:list-none',
  '[&_ol>ol:not(.contains-task-list)]:[list-style-type:lower-alpha]',
  '[&_ol>ol>ol:not(.contains-task-list)]:[list-style-type:lower-roman]',
  '[&_.task-list-item-checkbox]:mr-2 [&_.task-list-item-checkbox]:mt-0.5 [&_.task-list-item-checkbox]:shrink-0 [&_.task-list-item-checkbox]:align-top',
].join(' ');
</script>

<template>
  <div
    ref="host"
    class="flex w-full min-w-0 flex-col gap-4"
    data-testid="assistant-message-side-panel-content"
  >
    <template v-for="(segment, idx) in groupedSegments" :key="idx">
      <div
        v-if="segment.type === 'markdown'"
        v-sanitize-html="renderMarkdown(segment.content)"
        :class="markdownHostClass"
        @click="handleMarkdownClick"
      />
      <div
        v-else-if="segment.type === 'widget-group'"
        class="mb-0 flex w-full flex-wrap gap-4"
        data-testid="widget-group"
      >
        <template v-for="id in segment.ids" :key="id">
          <WidgetWrapper
            v-if="widgetMap.has(id)"
            :widgets="[widgetMap.get(id)!]"
            @on-widget-click="emit('messageInteraction', $event)"
          />
        </template>
      </div>
    </template>
  </div>
</template>
